import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useNavigate, useLocation } from 'react-router-dom';
import { RootState } from '../../../../store';
import { selectChat, deleteChat, loadChatsPage } from '../../../../chatSlice';

import { clearUser } from '../../../../userSlice';
import { clearTokens, getCachedApiUrl } from '../../../utils/apiUtils';
import { hasProtocolsAccess, hasCopilotAccess } from '../../../utils/accessUtils';
import { useSidebar } from '../../../contexts/SidebarContext';
import { useRenameChat } from '../../../contexts/RenameChatContext';
import { useChatSync } from '../../../hooks/useChatSync';
import { useDocumentPiP } from '../../../hooks/useDocumentPiP';
import MedicalAssistantPiP from '../../../components/MedicalAssistantPiP';
import sidebarOpen from '../../../../assets/sidebar-open.svg';
import chatAdd from '../../../../assets/chat-add.svg';
import statisticsIcon from '../../../../assets/statistics.svg';
import adminIcon from '../../../../assets/admin.svg';
import protocolsIcon from '../../../../assets/protocols.svg';
import medicalAssistantIcon from '../../../../assets/medical-assistant.svg';
import ChatListItem from './ChatListItem';
import ProfileDialog from './ProfileDialog';
import { useBrand } from '../../../../brands/contexts/BrandContext';
import { useI18n } from '../../../contexts/I18nContext';

interface SidebarProps {
    onSelectChat?: (chatId: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onSelectChat }): React.ReactElement => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { chats, selectedChatId, totalChats } = useSelector((state: RootState) => state.chat);
    const user = useSelector((state: RootState) => state.user);
    const [searchQuery, setSearchQuery] = useState('');
    const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
    const { isSidebarOpen, toggleSidebar, isMobile } = useSidebar();
    const { openRenameDialog } = useRenameChat();
    const { isSupported: isDocumentPiPSupported, isOpen: isDocumentPiPOpen, openPiP: openDocumentPiP, closePiP: closeDocumentPiP } = useDocumentPiP();
    const { brand } = useBrand();
    const { t } = useI18n();

    const PAGE_SIZE = 10;

    // Используем новые хуки для синхронизации
    const { createChat, deleteChat: deleteChatOnServer, isOnline, isLoading } = useChatSync();

    // Определяем активный маршрут
    const isDialogsActive = location.pathname === '/dialogs';
    const isProtocolsActive = location.pathname === '/protocols' || location.pathname.startsWith('/protocols/');
    const isStatisticsActive = location.pathname === '/statistics';
    const isAdminActive = location.pathname === '/admin';
    const isSubscriptionActive = location.pathname.startsWith('/subscription');

    // Получаем пути к логотипам из конфигурации бренда
    const getLogoPath = (isFullLogo: boolean) => {
        const logoPath = isFullLogo ? brand.logos.full : brand.logos.small;
        return `${process.env.PUBLIC_URL}/${logoPath}`;
    };

    const handleAddChat = async () => {
        try {
            const newChat = await createChat(t('sidebar.newChat'));
            if (newChat) {
                // addChat и selectChat уже вызываются в createChatOnServer.fulfilled
                navigate('/dialogs');
            } else {
                console.warn('Не удалось создать чат');
            }
        } catch (error) {
            console.error('Ошибка создания чата:', error);
        }
    };

    const handleSelectChat = (chatId: string) => {
        // Всегда переходим в диалоги при выборе чата
        dispatch(selectChat(chatId));
        navigate('/dialogs');
        // Закрываем sidebar на мобильных устройствах после выбора чата
        if (isMobile && isSidebarOpen) {
            toggleSidebar();
        }
        if (onSelectChat) {
            onSelectChat(chatId);
        }
    };

    const handleDeleteChat = async (chatId: string) => {
        try {
            // Сначала удаляем на сервере (если онлайн)
            if (isOnline) {
                await deleteChatOnServer(chatId);
            }
            // Затем удаляем локально
            dispatch(deleteChat(chatId));
        } catch (error) {
            console.error('Ошибка удаления чата:', error);
            // В случае ошибки все равно удаляем локально
            dispatch(deleteChat(chatId));
        }
    };

    const handleProfileClick = () => {
        setIsProfileDialogOpen(true);
    };

    const handleLogout = async () => {
        try {
            // Очищаем JWT токены
            clearTokens();
        } catch (error) {
            console.warn('Ошибка при logout:', error);
        } finally {
            dispatch(clearUser());
            setIsProfileDialogOpen(false);
            // Принудительно обновляем страницу для синхронизации с useAuth хуком
            // Это обеспечит корректную работу Redirector компонента
            window.location.href = '/auth';
        }
    };

    const handleStatisticsClick = () => {
        // Сбрасываем выбранный чат при переходе в статистику
        dispatch(selectChat(''));
        navigate('/statistics');
    };

    const handleAdminClick = () => {
        // Сбрасываем выбранный чат при переходе в админ панель
        dispatch(selectChat(''));
        navigate('/admin');
    };

    const handleCopilotClick = async () => {
        if (!isDocumentPiPSupported) {
            // Если Document PiP не поддерживается, не показываем кнопку
            return;
        }

        if (isDocumentPiPOpen) {
            closeDocumentPiP();
            return;
        }

        try {
            await openDocumentPiP(<MedicalAssistantPiP />, { width: 400, height: 500 });
        } catch (error) {
            console.error('Failed to open Document PiP', error);
        }
    };

    const handleProtocolsClick = () => {
        // Сбрасываем выбранный чат при переходе в протоколы
        dispatch(selectChat(''));
        navigate('/protocols');
    };

    const handleSubscriptionClick = () => {
        dispatch(selectChat(''));
        navigate('/subscription');
        if (isMobile && isSidebarOpen) {
            toggleSidebar();
        }
    };

    const handleLongPressChat = (chatId: string) => {
        const chat = chats.find((c: import('../../../../chatSlice').Chat) => c.id === chatId);
        if (chat) {
            openRenameDialog(chatId, chat.title);
        }
    };

    const filteredChats = chats
        .filter((chat: { title: string; id: string }) =>
            chat.title.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a: import('../../../../chatSlice').Chat, b: import('../../../../chatSlice').Chat) => {
            // Сортируем по времени последнего сообщения от новых к старым
            const timeA = new Date(a.lastMessageTime).getTime();
            const timeB = new Date(b.lastMessageTime).getTime();
            return timeB - timeA; // Обратный порядок для сортировки от новых к старым
        });

    const [visiblePages, setVisiblePages] = useState(1);

    const visibleChats = filteredChats.slice(0, visiblePages * PAGE_SIZE);

    const hasMoreInStore = visibleChats.length < filteredChats.length;
    const hasMoreOnServer = filteredChats.length < (totalChats || 0);

    const handleShowMore = () => {
        // Сначала показываем уже загруженные, но скрытые чаты
        if (hasMoreInStore) {
            setVisiblePages(prev => prev + 1);
            return;
        }

        // Если все загруженные чаты уже показаны, но на сервере есть ещё — догружаем следующую страницу
        if (hasMoreOnServer) {
            const nextSkip = chats.length;
            const nextLimit = PAGE_SIZE;
            (dispatch as any)(loadChatsPage({ skip: nextSkip, limit: nextLimit }));
            setVisiblePages(prev => prev + 1);
        }
    };

    return (
        <>
            <div id='sidebar' className={`sidebar ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
                <div id='sidebar-list' className='d-flex flex-column justify-content-between'>
                    <div className='sidebar-top'>
                        <div className={`${isSidebarOpen ? 'w-100 d-flex flex-row align-items-center justify-content-between' : 'd-flex flex-column align-items-center justify-content-center'} `}>
                            <div
                                id='sidebar-list-logo'
                                className='mb-3'
                                style={{
                                    width: isSidebarOpen ? '160px' : '32px',
                                    height: isSidebarOpen ? '24px' : '32px',
                                    backgroundImage: `url(${getLogoPath(isSidebarOpen)})`,
                                    backgroundSize: 'contain',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'center',
                                    cursor: 'pointer'
                                }}
                            ></div>
                            <button
                                className='sidebar-toggle-btn mb-3'
                                onClick={toggleSidebar}
                                title={isSidebarOpen ? t('sidebar.toggle.close') : t('sidebar.toggle.open')}
                            >
                                <img src={sidebarOpen} alt="Toggle sidebar" />
                            </button>
                        </div>
                        {isSidebarOpen && (
                            <div className='w-100 d-flex flex-row align-items-center justify-content-between '>
                                <input
                                    className='input-secondary w-100 me-2'
                                    placeholder={t('sidebar.search.placeholder')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <button className='chat-add-btn' title={t('sidebar.newChat')} onClick={handleAddChat}>
                                    <img src={chatAdd} alt="Add chat" />
                                </button>
                            </div>
                        )}
                    </div>

                    {isSidebarOpen && (
                        <div className='sidebar-content'>
                            <div className='chat-history'>
                                <h6 className='chat-history-title'>{t('sidebar.chats')}</h6>
                                <div className='chat-list'>
                                    {isLoading ? (
                                        <div className='chat-item-empty'>
                                            <div className='chat-loader'>
                                                <div className='spinner'></div>
                                                <p>{t('sidebar.loadingChats')}</p>
                                            </div>
                                        </div>
                                    ) : filteredChats.length > 0 ? (
                                        <>
                                            {visibleChats.map((chat: import('../../../../chatSlice').Chat) => (
                                                <ChatListItem
                                                    key={chat.id}
                                                    chat={chat}
                                                    active={chat.id === selectedChatId && isDialogsActive}
                                                    onClick={handleSelectChat}
                                                    onDelete={handleDeleteChat}
                                                    onLongPress={handleLongPressChat}
                                                />
                                            ))}
                                            {(hasMoreInStore || hasMoreOnServer) && (
                                                <div className='d-flex flex-row align-items-center justify-content-center'>
                                                    <a
                                                        className='chat-list-load-more text-decoration-none'
                                                        onClick={handleShowMore}
                                                    >
                                                        {t('sidebar.showMore')}
                                                    </a>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className='chat-item-empty'>
                                            <p>{t('sidebar.noChats')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div id='' className='sidebar-bottom'>
                        {isDocumentPiPSupported && hasCopilotAccess(user) && (
                            <button
                                className={`statistics-button medical-assistant-btn ${isDocumentPiPOpen ? 'active' : ''}`}
                                onClick={handleCopilotClick}
                                title={isDocumentPiPOpen ? t('sidebar.copilot.close') : t('sidebar.copilot.open')}
                            >
                                <img src={medicalAssistantIcon} alt="Copilot" />
                                {isSidebarOpen && <span className='statistics-text'>{t('sidebar.copilot')}</span>}
                            </button>
                        )}
                        {hasProtocolsAccess(user) && (
                            <button
                                className={`statistics-button ${isProtocolsActive ? 'active' : ''}`}
                                onClick={handleProtocolsClick}
                            >
                                <img src={protocolsIcon} alt="Protocols" />
                                {isSidebarOpen && <span className='statistics-text'>{t('sidebar.templates')}</span>}
                            </button>
                        )}
                        {hasProtocolsAccess(user) && (
                            <button
                                className={`statistics-button ${isSubscriptionActive ? 'active' : ''}`}
                                onClick={handleSubscriptionClick}
                                title={t('sidebar.subscription')}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                    <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" fill="currentColor" />
                                </svg>
                                {isSidebarOpen && <span className='statistics-text'>{t('sidebar.subscription')}</span>}
                            </button>
                        )}
                        {user && user.role_id >= 3 && (
                            <button
                                className={`statistics-button ${isStatisticsActive ? 'active' : ''}`}
                                onClick={handleStatisticsClick}
                            >
                                <img src={statisticsIcon} alt="Statistics" />
                                {isSidebarOpen && <span className='statistics-text'>{t('sidebar.statistics')}</span>}
                            </button>
                        )}
                        {user && (user.role_id === 3 || user.role_id === 4) && (
                            <button
                                className={`statistics-button ${isAdminActive ? 'active' : ''}`}
                                onClick={handleAdminClick}
                            >
                                <img src={adminIcon} alt="Admin" />
                                {isSidebarOpen && <span className='admin-text'>{t('sidebar.admin')}</span>}
                            </button>
                        )}
                        <button className='profile-button' onClick={handleProfileClick}>
                            <div className='avatar-link'>
                                {user?.avatar_url && (
                                    <img 
                                        src={`${getCachedApiUrl()}${user.avatar_url}`} 
                                        alt="Avatar" 
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: '50%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                )}
                            </div>
                            {isSidebarOpen && <span className='profile-text'>{t('sidebar.profile')}</span>}
                        </button>
                    </div>
                </div>
            </div>

            <ProfileDialog
                isOpen={isProfileDialogOpen}
                onClose={() => setIsProfileDialogOpen(false)}
                onLogout={handleLogout}
            />

        </>
    );
}

export default Sidebar;